import React, { useState } from 'react';
import { CreditCard, Tag } from 'lucide-react';
import { Disclosure, DisclosureContent, DisclosureTrigger } from '@gialaboratory/sdk/ui';

type TRetailerToolIcon = 'card' | 'tag';

type TRetailerToolItem = {
    readonly href: string;
    readonly label: string;
    readonly description?: string | undefined;
    readonly icon: TRetailerToolIcon;
};

type TRetailerToolsDisclosureProps = {
    readonly items: readonly TRetailerToolItem[];
    readonly triggerLabel: string;
    readonly headingLevel?: 2 | 3 | 4 | 5 | 6;
    readonly children?: React.ReactNode;
};

const ICONS: Record<TRetailerToolIcon, React.ReactNode> = {
    card: <CreditCard size={18} strokeWidth={2} className='shrink-0' aria-hidden='true' />,
    tag: <Tag size={18} strokeWidth={2} className='shrink-0' aria-hidden='true' />,
};

const FOCUS_RING =
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--text-blue)]';

const RetailerToolsDisclosure: React.FC<TRetailerToolsDisclosureProps> = ({
    items,
    triggerLabel,
    headingLevel = 3,
    children,
}) => {
    const [open, setOpen] = useState<boolean>(false);

    return (
        <div
            className={`relative border border-border bg-surface px-4 py-2 text-left font-arial transition-[border-radius] duration-150 motion-reduce:transition-none ${
                open ? 'rounded-b-none rounded-t-lg' : 'rounded-lg'
            }`}
        >
            {children}
            {items.length > 0 && (
                <Disclosure overlay open={open} onOpenChange={setOpen} className='border-t border-border pt-1.5'>
                    <DisclosureTrigger
                        headingLevel={headingLevel}
                        headingClassName='font-arial text-[12px] font-semibold normal-case leading-[1.6] tracking-normal'
                        className='text-foreground-muted'
                    >
                        <span>{triggerLabel}</span>
                    </DisclosureTrigger>
                    <DisclosureContent
                        asChild
                        className='-inset-x-px -mt-1 rounded-b-lg border border-t-0 border-border px-4 pb-3 text-left shadow-md'
                    >
                        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- WebKit/VoiceOver drops list semantics when list-style is none */}
                        <ul role='list' className='m-0 list-none p-0'>
                            {items.map(({ href, label, description, icon }) => (
                                <li key={href}>
                                    <a
                                        href={href}
                                        target='_blank'
                                        rel='noreferrer'
                                        className={`flex min-h-6 scroll-mb-52 flex-col gap-0.5 rounded px-2 py-2 font-arial text-[12px] normal-case tracking-normal hover:bg-[color:var(--color-surface-secondary)] ${FOCUS_RING}`}
                                    >
                                        <span className='flex items-center gap-2 font-arial text-[12px] font-medium text-[color:var(--text-blue)]'>
                                            {ICONS[icon]}
                                            <span>{label}</span>
                                        </span>
                                        {description && (
                                            <span className='font-arial text-[10px] font-normal text-foreground-muted'>
                                                {description}
                                            </span>
                                        )}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </DisclosureContent>
                </Disclosure>
            )}
        </div>
    );
};

export type { TRetailerToolIcon, TRetailerToolItem, TRetailerToolsDisclosureProps };
export default RetailerToolsDisclosure;
